import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { propertyApi } from '@/api/property';
import { logger } from '@/utils/debugLogger';
import type { PropertySearchResult, PropertyBoundary } from '@/types/property';

const RECENT_SEARCHES_STORAGE_KEY = 'recentSearches';
const SELECTED_PROPERTY_STORAGE_KEY = 'forestModeler_selectedProperty';
const PROPERTY_BOUNDARY_STORAGE_KEY = 'forestModeler_propertyBoundary';

function readStorageValue<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorageValue<T>(key: string, value: T | null): void {
  if (value === null) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(value));
}

interface PropertyState {
  // Search state
  searchQuery: string;
  searchResults: PropertySearchResult[];
  isSearching: boolean;
  selectedProperty: PropertySearchResult | null;

  // Property boundary
  propertyBoundary: PropertyBoundary | null;
  isFetchingBoundary: boolean;

  // Recent searches
  recentSearches: string[];

  // Actions
  setSearchQuery: (query: string) => void;
  searchProperties: (query: string, limit?: number) => Promise<void>;
  selectProperty: (property: PropertySearchResult) => void;
  fetchPropertyBoundary: (propertyId: string) => Promise<PropertyBoundary | null>;
  clearSelection: () => void;
  addRecentSearch: (query: string) => void;
}

export const usePropertyStore = create<PropertyState>()(
  devtools(
    (set, get) => ({
      // Initial state
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      selectedProperty: readStorageValue<PropertySearchResult | null>(
        SELECTED_PROPERTY_STORAGE_KEY,
        null,
      ),
      propertyBoundary: readStorageValue<PropertyBoundary | null>(
        PROPERTY_BOUNDARY_STORAGE_KEY,
        null,
      ),
      isFetchingBoundary: false,
      recentSearches: readStorageValue<string[]>(RECENT_SEARCHES_STORAGE_KEY, []),

      // Actions
      setSearchQuery: (query) => {
        set({ searchQuery: query });
      },

      searchProperties: async (query, bufferMeters = 250) => {
        if (!query.trim()) {
          set({ searchResults: [] });
          return;
        }

        set({ isSearching: true });
        
        try {
          // Use the backend's property search format
          const response = await propertyApi.search(query.trim(), bufferMeters);
          
          // Use backend-provided center_coordinates (lat, lng)
          const [centerLat, centerLng] = response.property.center_coordinates;

          const searchResult = {
            property_id: response.property.property_id,
            address: response.property.display_property_id,
            // municipality: undefined, // NOT PROVIDED BY BACKEND // Backend doesn't provide this yet
            // region: omitted when backend does not supply it (NO-FALLBACK rule)
            coordinates: {
              lat: centerLat,
              lng: centerLng,
            },
            confidence_score: 1.0, // Perfect match for property ID search
            area_hectares: response.property.area_sqm / 10000,
            parcels_count: response.property.parcels_count,
            bounds: response.bbox
          };
          
          set({ 
            searchResults: [searchResult], // Backend returns single property
            isSearching: false 
          });
          
          get().addRecentSearch(query.trim());
        } catch (error) {
          logger.error(
            { component: 'PropertyStore', action: 'searchProperties' },
            'Property search failed',
            error
          );
          set({ 
            searchResults: [], 
            isSearching: false 
          });
          throw error;
        }
      },

      selectProperty: (property) => {
        // Drop any boundary that belongs to a DIFFERENT property. Consumers
        // (e.g. MapView) resolve the active property id from
        // propertyBoundary.property.property_id BEFORE selectedProperty.property_id,
        // so a stale boundary from a previously-viewed property would otherwise
        // win and load the wrong forest's context. Callers that have an
        // authoritative boundary (search flow) re-fetch it immediately after.
        const staleBoundary = get().propertyBoundary;
        const boundaryIsStale =
          staleBoundary != null &&
          staleBoundary.property?.property_id !== property.property_id;

        if (boundaryIsStale) {
          set({ selectedProperty: property, propertyBoundary: null });
          writeStorageValue(PROPERTY_BOUNDARY_STORAGE_KEY, null);
        } else {
          set({ selectedProperty: property });
        }
        writeStorageValue(SELECTED_PROPERTY_STORAGE_KEY, property);
      },

      fetchPropertyBoundary: async (propertyId) => {
        set({ isFetchingBoundary: true });
        
        try {
          const boundary = await propertyApi.getBoundary(propertyId);
          set({ 
            propertyBoundary: boundary, 
            isFetchingBoundary: false 
          });
          writeStorageValue(PROPERTY_BOUNDARY_STORAGE_KEY, boundary);
          return boundary;
        } catch (error) {
          logger.error(
            { component: 'PropertyStore', action: 'fetchPropertyBoundary', propertyId },
            'Failed to fetch property boundary',
            error
          );
          set({ 
            propertyBoundary: null, 
            isFetchingBoundary: false 
          });
          throw error;
        }
      },

      clearSelection: () => {
        set({ 
          selectedProperty: null, 
          propertyBoundary: null 
        });
        writeStorageValue(SELECTED_PROPERTY_STORAGE_KEY, null);
        writeStorageValue(PROPERTY_BOUNDARY_STORAGE_KEY, null);
      },

      addRecentSearch: (query) => {
        const current = get().recentSearches;
        const updated = [query, ...current.filter(q => q !== query)].slice(0, 5);
        set({ recentSearches: updated });
        writeStorageValue(RECENT_SEARCHES_STORAGE_KEY, updated);
      },
    }),
    {
      name: 'property-store',
    }
  )
);
